import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import {
  ConversationPlatform,
  ExpandedConversation,
  ExpandedConversationMessage,
  findLastConvertedMessage,
  MessageVisibility,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { EmailReplyType } from "@redotech/redo-model/support/conversations/email-info";
import { ErrorMessageSection } from "@redotech/redo-web/error-page";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback, useEffect, useState } from "react";
import { useIntercom } from "react-use-intercom";
import { ConversationEmailMessagesView } from "./conversation-email-view/conversation-email-messages-view";
import { EmailDraftState } from "./conversation-email-view/email-draft-state";
import { ConversationHeader } from "./conversation-header";
import { ConversationMessages } from "./conversation-messages";
import { MessageInput } from "./message-input/message-input";
import { OfferVoiceConversion } from "./offer-voice-conversion";

export const ConversationContent = memo(function ConversationContent({
  fullConversation,
  fullConversationLoading,
  conversation,
  setActiveConversation,
  conversationJustMerged,
  actionButtons,
  leftPanelOpen,
  rightPanelOpen,
  handleSetLeftPanelOpen,
  handleSetRightPanelOpen,
  onConversationsViewed,
  removeConversationFromProximity,
  nextConversationInList,
  prevConversationInList,
  pendingNextConversation,
}: {
  conversationJustMerged?: boolean;
  fullConversation: ExpandedConversation | undefined;
  fullConversationLoading: boolean;
  conversation: MinimalConversation;
  setActiveConversation: (
    conversation: MinimalConversation | undefined,
  ) => void;
  actionButtons: React.ReactNode;
  leftPanelOpen: boolean;
  rightPanelOpen: boolean;
  handleSetLeftPanelOpen: (value: boolean) => void;
  handleSetRightPanelOpen: (value: boolean) => void;
  onConversationsViewed: (
    conversation: MinimalConversation,
  ) => MinimalConversation | undefined;
  removeConversationFromProximity: (
    conversationToExclude: MinimalConversation,
  ) => void;
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
  pendingNextConversation?: boolean;
}) {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [typing, setTyping] = useState<Record<string, Date>>({});
  const [showFullCommentThread, setShowFullCommentThread] = useState(true);

  const [
    lastCustomerMessageIdBeforeConversion,
    setLastCustomerMessageIdBeforeConversion,
  ] = useState<string | undefined>(undefined);
  const [messageAfterConversionMessage, setMessageAfterConversionMessage] =
    useState<ExpandedConversationMessage | null>(null);
  const [convertedEmailDraft, setConvertedEmailDraft] =
    useState<EmailDraftState>({ status: "noDraft" });
  const [
    shouldPopupRecipientsModalBecauseForwardButtonClicked,
    setShouldPopupRecipientsModalBecauseForwardButtonClicked,
  ] = useState(false);
  const [shouldShowDraftInfo, setShouldShowDraftInfo] =
    useState<boolean>(false);

  // Only to be used for the first message after a platform conversion
  const getDraftContent = useCallback(() => {
    const msg = conversation.latestDraftMessage;
    return msg?.content || "";
  }, [conversation.latestDraftMessage]);

  const initializeConvertedEmailDraft = useCallback(
    (mode: EmailReplyType = EmailReplyType.REPLY) => {
      setConvertedEmailDraft({
        status: "drafting",
        mode,
        draft: {
          recipientsInfo: {
            to: getPrimaryCustomerEmail(conversation.customer)
              ? [{ email: getPrimaryCustomerEmail(conversation.customer)! }]
              : [],
            cc: [],
            bcc: [],
            inReplyToMongoId:
              lastCustomerMessageIdBeforeConversion ??
              conversation.lastMessage?._id,
          },
          subject: `Re: ${conversation.subject}`,
          content: getDraftContent(),
        },
      });
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      lastCustomerMessageIdBeforeConversion,
      conversation.customer?._id,
      conversation.subject,
      conversation._id,
    ],
  );

  useEffect(() => {
    if (shouldShowDraftInfo) {
      initializeConvertedEmailDraft();
    } else {
      setConvertedEmailDraft({ status: "noDraft" });
    }
  }, [
    setConvertedEmailDraft,
    shouldShowDraftInfo,
    initializeConvertedEmailDraft,
  ]);

  useEffect(() => {
    if (fullConversationLoading || !fullConversation) {
      return;
    }

    if (
      fullConversation.originalPlatform &&
      fullConversation.platform !== fullConversation.originalPlatform
    ) {
      const lastConversionMessage = findLastConvertedMessage(
        fullConversation.messages,
      );

      if (lastConversionMessage) {
        const lastCustomerMessageBeforeConversion = fullConversation.messages
          .reverse()
          .find((msg, index, reversedMessages) => {
            const isFirstMessage = index === reversedMessages.length - 1;
            return (
              msg.createdAt < lastConversionMessage.createdAt &&
              (msg.type === "customer" || isFirstMessage) &&
              msg.visibility === MessageVisibility.PUBLIC
            );
          });
        setLastCustomerMessageIdBeforeConversion(
          lastCustomerMessageBeforeConversion?._id,
        );
        const firstMessageAfterConversionMessage =
          fullConversation.messages.find((message) => {
            return (
              message.createdAt > lastConversionMessage.createdAt &&
              !message.draftInfo?.isDraft &&
              message.visibility === MessageVisibility.PUBLIC
            );
          });
        setMessageAfterConversionMessage(
          firstMessageAfterConversionMessage || null,
        );
      }
    } else {
      setLastCustomerMessageIdBeforeConversion(undefined);
      setMessageAfterConversionMessage(null);
    }
    // FIXME
  }, [fullConversationLoading, fullConversation]);

  const handleSetReplyDraft = (
    newDraftStateOrUpdater: React.SetStateAction<EmailDraftState>,
  ) => {
    const newState =
      typeof newDraftStateOrUpdater === "function"
        ? newDraftStateOrUpdater(convertedEmailDraft)
        : newDraftStateOrUpdater;

    if (newState.status === "drafting" && !newState.draft.content) {
      newState.draft.content = getDraftContent();
    }

    setConvertedEmailDraft(newState);
  };

  const setTriggerReinitalizeDraft: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    const shouldReinitialize =
      typeof value === "function" ? value(false) : value;

    if (shouldReinitialize) {
      const currentMode =
        convertedEmailDraft.status === "drafting"
          ? convertedEmailDraft.mode
          : EmailReplyType.REPLY;

      initializeConvertedEmailDraft(currentMode);
    }
  };

  // if the last message is a convert from chat to email or only has internal notes we want to fill in draft props
  useEffect(() => {
    const emailConversationHasOnlyInternalNotes =
      conversation.platform === ConversationPlatform.EMAIL &&
      conversation.messages.every(
        (msg) =>
          msg.visibility === MessageVisibility.INTERNAL ||
          msg.draftInfo?.isDraft,
      );

    setShouldShowDraftInfo(
      (!messageAfterConversionMessage &&
        !!lastCustomerMessageIdBeforeConversion) ||
        emailConversationHasOnlyInternalNotes,
    );
  }, [
    conversation._id,
    conversation.platform,
    conversation.messages,
    messageAfterConversionMessage,
    lastCustomerMessageIdBeforeConversion,
  ]);

  const intercom = useIntercom();

  return (
    <>
      <ConversationHeader
        actionButtons={actionButtons}
        conversation={conversation}
        handleSetLeftPanelOpen={handleSetLeftPanelOpen}
        handleSetRightPanelOpen={handleSetRightPanelOpen}
        leftPanelOpen={leftPanelOpen}
        nextConversationInList={nextConversationInList}
        onConversationsViewed={onConversationsViewed}
        pendingNextConversation={pendingNextConversation}
        prevConversationInList={prevConversationInList}
        rightPanelOpen={rightPanelOpen}
        setActiveConversation={setActiveConversation}
      />
      {conversation.messages.length === 0 ? (
        <Flex align="center" height="full" justify="center" px="lg">
          <ErrorMessageSection
            details={`This conversation (id ${conversation._id}) has no messages. Please contact Redo support.`}
            showNewIntercomMessage={() => intercom.showNewMessage()}
          />
        </Flex>
      ) : (
        <>
          {conversation.platform === ConversationPlatform.EMAIL ? (
            <ConversationEmailMessagesView
              conversation={conversation}
              conversationJustMerged={conversationJustMerged}
              fullConversation={fullConversation}
              fullConversationLoading={fullConversationLoading}
              nextConversationInList={nextConversationInList}
              prevConversationInList={prevConversationInList}
              removeConversationFromProximity={removeConversationFromProximity}
              setActiveConversation={setActiveConversation}
              setErrorMessage={setErrorMessage}
              setShowErrorMessage={setShowErrorMessage}
              setShowFullCommentThread={setShowFullCommentThread}
              setTyping={setTyping}
              showFullCommentThread={showFullCommentThread}
              typing={typing}
            />
          ) : (
            <ConversationMessages
              conversation={conversation}
              fullConversation={fullConversation}
              fullConversationLoading={fullConversationLoading}
              setActiveConversation={setActiveConversation}
              setErrorMessage={setErrorMessage}
              setShowErrorMessage={setShowErrorMessage}
              showFullCommentThread={showFullCommentThread}
              typing={typing}
            />
          )}
          {fullConversation?.platform === ConversationPlatform.VOICE && (
            <OfferVoiceConversion
              conversation={fullConversation}
              onConversationUpdated={setActiveConversation}
            />
          )}
          {fullConversation?._id ? (
            <MessageInput
              conversation={fullConversation}
              emailDraftProps={
                shouldShowDraftInfo && convertedEmailDraft.status === "drafting"
                  ? {
                      draftInfo: convertedEmailDraft,
                      handleSetReplyDraft,
                      setTriggerReinitalizeDraft,
                      shouldPopupRecipientsModalBecauseForwardButtonClicked,
                      setShouldPopupRecipientsModalBecauseForwardButtonClicked,
                    }
                  : undefined
              }
              isFirstMessageAfterPlatformConversion={
                !messageAfterConversionMessage
              }
              key={fullConversation?._id}
              messageIdForKey={lastCustomerMessageIdBeforeConversion}
              nextConversationInList={nextConversationInList}
              prevConversationInList={prevConversationInList}
              removeConversationFromProximity={removeConversationFromProximity}
              rightPanelOpen={rightPanelOpen}
              setActiveConversation={setActiveConversation}
              setErrorMessage={setErrorMessage}
              setShowErrorMessage={setShowErrorMessage}
              setShowFullCommentThread={setShowFullCommentThread}
              setTyping={setTyping}
              showFullCommentThread={showFullCommentThread}
              typing={typing}
            />
          ) : (
            <div />
          )}
        </>
      )}
      {showErrorMessage && (
        <Snackbar
          autoHideDuration={10_000}
          onClose={() => setShowErrorMessage(false)}
          open={showErrorMessage}
        >
          <Alert onClose={() => setShowErrorMessage(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
});
